<template>
  <div class="order-summary">
    <!-- 左侧：项目汇总 -->
    <div class="summary-section">
      <h4 class="section-title">{{ $t('buy.summary.projectSummary') }}</h4>

      <!-- Step 1: Plan -->
      <div v-if="activeStep === 0" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedPlan') }}</span>
          <span class="value">{{ planName }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.billingCycle') }}</span>
          <span class="value">{{ billingLabel }}</span>
        </div>
      </div>

      <!-- Step 2: Standards -->
      <div v-else-if="activeStep === 1" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedPlan') }}</span>
          <span class="value">{{ planName }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.billingCycle') }}</span>
          <span class="value">{{ billingLabel }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedLanguages') }}</span>
          <span class="value">{{ languagesCount }}
            <span v-if="selectedLanguages.length > 0"> ({{ languagesNames }})</span>
          </span>
        </div>
      </div>

      <!-- Step 3: Standards -->
      <div v-else-if="activeStep === 2" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedPlan') }}</span>
          <span class="value">{{ planName }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.billingCycle') }}</span>
          <span class="value">{{ billingLabel }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedLanguages') }}</span>
          <span class="value">{{ languagesCount }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedStandards') }}</span>
          <span class="value">{{ standardsCount }}
            <span v-if="selectedStandards.length > 0"></span>
          </span>
        </div>
      </div>

      <!-- Step 4, 5, 6: Full summary -->
      <div v-else class="summary-items">
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedPlan') }}</span>
          <span class="value">{{ planName }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.billingCycle') }}</span>
          <span class="value">{{ billingLabel }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedLanguages') }}</span>
          <span class="value">{{ languagesCount }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedStandards') }}</span>
          <span class="value">{{ standardsCount }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.auditorSeats') }}</span>
          <span class="value">{{ totalAuditorSeats }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.supplierSeats') }}</span>
          <span class="value">{{ $t('buy.summary.unlimited') }}</span>
        </div>
      </div>
    </div>

    <!-- 右侧：价格汇总 -->
    <div class="summary-section price-section">
      <h4 class="section-title">{{ $t('buy.summary.priceSummary') }}</h4>

      <!-- Step 1: Only plan price -->
      <div v-if="activeStep === 0" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ planName }}</span>
          <span class="value">{{ formatPrice(orderData.planPrice) }}</span>
        </div>
        <div class="summary-row highlight">
          <span class="label">{{ $t('buy.summary.estimatedTotal') }}</span>
          <span class="value total">{{ formatPrice(planPrice) }}</span>
        </div>
      </div>

      <!-- Step 2: Plan + Standards -->
      <div v-else-if="activeStep === 1" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ planName }}</span>
          <span class="value">{{ formatPrice(orderData.planPrice) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedLanguages') }}</span>
          <span class="value">{{ languagesCount }}</span>
        </div>
        <div class="summary-row highlight">
          <span class="label">{{ $t('buy.summary.estimatedTotal') }}</span>
          <span class="value total">{{ formatPrice(planPrice) }}</span>
        </div>
      </div>

      <!-- Step 3: Plan + Standards -->
      <div v-else-if="activeStep === 2" class="summary-items">
        <div class="summary-row">
          <span class="label">{{ planName }}</span>
          <span class="value">{{ formatPrice(orderData.planPrice) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedStandards') }}</span>
          <span class="value">{{ formatPrice(standardsPrice) }}</span>
        </div>
        <div class="summary-row highlight">
          <span class="label">{{ $t('buy.summary.estimatedTotal') }}</span>
          <span class="value total">{{ formatPrice(planPrice + standardsPrice) }}</span>
        </div>
      </div>

      <!-- Step 4, 5, 6: Full pricing -->
      <div v-else class="summary-items">
        <div class="summary-row">
          <span class="label">{{ planName }}</span>
          <span class="value">{{ formatPrice(orderData.planPrice) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.summary.selectedStandards') }}</span>
          <span class="value">{{ formatPrice(standardsPrice) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">{{ $t('buy.licences.seatsCost') }}</span>
          <span class="value">{{ formatPrice(seatsPrice) }}</span>
        </div>
        <div class="summary-row highlight">
          <span class="label">{{ $t('buy.summary.estimatedTotal') }}</span>
          <span class="value total">{{ formatPrice(planPrice + standardsPrice + seatsPrice) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { displayCurrencyLabel, formatPrice as formatPriceValue } from '@/utils/currency'
export default {
  name: 'OrderSummary',
  props: {
    orderData: {
      type: Object,
      default: () => ({})
    },
    purchaseConfig: {
      type: Object,
      default: null
    },
    activeStep: {
      type: Number,
      default: 0
    },
    selectedStandards: {
      type: Array,
      default: () => []
    },
    selectedLanguages: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    planName() {
      const plan = this.purchaseConfig && this.purchaseConfig.plans && this.purchaseConfig.plans[this.orderData.planId]
      if (this.purchaseConfig && this.purchaseConfig.plans && !plan) return this.$t('buy.plan.noPlans')
      const key = `buy.plan.plans.${this.orderData.planId}.name`
      return this.$te(key) ? this.$t(key) : (plan && plan.name) || this.orderData.planId
    },
    billingLabel() {
      return this.orderData.billingCycle === 'two_year'
        ? this.$t('buy.summary.year2')
        : this.$t('buy.summary.year1')
    },
    standardsCount() {
      return this.selectedStandards.length
    },
    standardsNames() {
      return this.selectedStandards.map(stdId => {
        return this.fullnameForStandard(stdId)
      }).join(', ')
    },
    languagesCount() {
      return this.selectedLanguages.length
    },
    languagesNames() {
      return this.selectedLanguages.map(languageId => this.languageLabel(languageId)).join(', ')
    },
    seatsPrice() {
      const base = this.orderData.seatsPrice || 0
      return this.orderData.billingCycle === 'two_year' ? base * 2 : base
    },
    standardsPrice() {
      const base = this.orderData.standardsPrice || 0
      return this.orderData.billingCycle === 'two_year' ? base * 2 : base
    },
    planPrice() {
      return this.orderData.planPrice || 0
    },
    totalAuditorSeats() {
      return Number(this.orderData.auditorSeats || 0) + Number(this.orderData.extraAdminSeats || 0)
    },
    displayCurrency() {
      const raw = (this.purchaseConfig && this.purchaseConfig.displayCurrency) || this.orderData.currency || 'RMB'
      return displayCurrencyLabel(raw)
    }
  },
  methods: {
    fullnameForStandard(stdId) {
      return this.$t(`buy.standards.items.${stdId}.fullname`)
    },
    languageLabel(languageId) {
      const label = this.$t(`buy.languages.items.${languageId}`)
      return label === `buy.languages.items.${languageId}` ? languageId : label
    },
    formatPrice(price) {
      if (typeof price !== 'number') return formatPriceValue(0)
      return `${this.displayCurrency} ${formatPriceValue(price)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.order-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.summary-section {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-radius: 14px;
  padding: 20px 24px;

  .section-title {
    font-size: 15px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #6366f1;
    padding-bottom: 8px;
  }
}

.summary-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .label {
    font-size: 14px;
    color: #64748b;
  }

  .value {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  &.highlight {
    padding-top: 12px;
    margin-top: 4px;
    border-top: 2px solid #e2e8f0 !important;

    .total {
      font-size: 20px;
      font-weight: 800;
      color: #6366f1;
    }
  }
}

@media screen and (max-width: 768px) {
  .order-summary {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-top: 16px;
    padding-top: 14px;
  }

  .summary-section {
    padding: 12px 14px;
    border-radius: 10px;

    .section-title {
      margin-bottom: 8px;
      padding-bottom: 6px;
      font-size: 12px;
      letter-spacing: 0;
    }
  }

  .summary-items {
    gap: 4px;
  }

  .summary-row {
    align-items: flex-start;
    gap: 8px;
    padding: 4px 0;

    .label,
    .value {
      font-size: 12px;
    }

    .value {
      text-align: right;
      word-break: break-word;
    }

    &.highlight {
      padding-top: 8px;

      .total {
        font-size: 16px;
      }
    }
  }
}
</style>
